import { initializeApp, type FirebaseApp } from 'firebase/app';
import {
  getAuth,
  initializeAuth,
  indexedDBLocalPersistence,
  browserLocalPersistence,
  browserSessionPersistence,
  browserPopupRedirectResolver,
  GoogleAuthProvider,
  OAuthProvider,
  signInWithPopup,
  signInWithCustomToken,
  signOut as fbSignOut,
  onIdTokenChanged,
  type Auth,
  type User,
} from 'firebase/auth';
import type { AuthClient, AuthProviderId, AuthSession } from './provider.js';
import { readFirebaseConfig, type FirebaseWebConfig } from './firebaseConfig.js';
import { localOnlyAuthClient } from './provider.js';
import { installE2EAuthHooks } from './e2eClient.js';
import { isTauri, isMobile } from '../shell/shell.js';
import { brokeredGoogleSignIn } from './mobileSignIn.js';
import { dbg } from '../debugOverlay.js';

/**
 * Firebase Identity Platform client (IMPL-03). Wraps the Firebase Auth SDK behind the AuthClient
 * port. Google uses the native provider; Apple is an OAuthProvider('apple.com') — both go through
 * the same popup flow, so the rest of the app is provider-agnostic. ID tokens auto-refresh; we read
 * the current one on demand via `user.getIdToken()`.
 */
function providerFor(id: AuthProviderId) {
  if (id === 'apple') {
    const p = new OAuthProvider('apple.com');
    p.addScope('email');
    p.addScope('name');
    return p;
  }
  return new GoogleAuthProvider();
}

function sessionFor(user: User): AuthSession {
  return {
    user: { uid: user.uid, email: user.email, displayName: user.displayName },
    getIdToken: () => user.getIdToken(),
  };
}

class FirebaseAuthClient implements AuthClient {
  readonly configured = true;
  private readonly auth: Auth;

  constructor(app: FirebaseApp) {
    // Persist the session across app restarts so the user signs in once per device. getAuth() can
    // fall back to in-memory persistence inside a WebView (losing the session on every close), so
    // pin durable persistence explicitly: IndexedDB → localStorage → session (first that works).
    try {
      // initializeAuth() (unlike getAuth()) does NOT auto-register a popupRedirectResolver, so pass
      // it explicitly — signInWithPopup on the web path throws auth/argument-error without it.
      this.auth = initializeAuth(app, {
        persistence: [indexedDBLocalPersistence, browserLocalPersistence, browserSessionPersistence],
        popupRedirectResolver: browserPopupRedirectResolver,
      });
    } catch {
      // Already initialized for this app (e.g. a second client in the same page) — reuse it.
      this.auth = getAuth(app);
    }
  }

  onChange(cb: (s: AuthSession | null) => void): () => void {
    return onIdTokenChanged(this.auth, (user) => cb(user ? sessionFor(user) : null));
  }

  async signIn(provider: AuthProviderId): Promise<void> {
    // Both the desktop and mobile shells run under Tauri (a webview can't use Firebase's popup), but
    // they need different flows: mobile (Android + iOS) polls a server-brokered custom token; desktop
    // runs a native loopback OAuth capture + signInWithCredential. The web path keeps the (unchanged)
    // popup.
    if (isTauri()) {
      if (isMobile()) {
        await this.signInNative(provider);
      } else {
        const { desktopSignIn } = await import('../shell/desktopLogin.js');
        await desktopSignIn(this.auth, provider);
      }
      return;
    }
    await signInWithPopup(this.auth, providerFor(provider));
  }

  /**
   * Server-brokered sign-in for the mobile shells (Android + iOS): the app can't complete a WebView
   * OAuth round-trip (tauri-plugin-deep-link doesn't surface custom-scheme redirects there), so the
   * server runs the Google flow and mints a Firebase custom token that the app polls for, then signs
   * in with it. See src/auth/mobileSignIn.ts + server-api /mobile-auth/*.
   */
  private async signInNative(provider: AuthProviderId): Promise<void> {
    if (provider !== 'google') {
      throw new Error('Only Google sign-in is available on mobile for now.');
    }
    const apiUrl = import.meta.env.VITE_API_URL as string | undefined;
    if (!apiUrl) {
      throw new Error('Sign-in is not configured (set VITE_API_URL).');
    }
    // Open the consent URL in the system browser (plugin-opener); poll our CORS-enabled API with the
    // WebView's own fetch (the API sends `access-control-allow-origin: *`).
    let openUrl: (url: string) => Promise<void> = async (url) => {
      window.open(url, '_blank');
    };
    try {
      openUrl = (await import('@tauri-apps/plugin-opener')).openUrl;
    } catch {
      /* keep the window.open fallback */
    }
    dbg('native sign-in: brokered flow');
    // Bind fetch to window: iOS's WKWebView throws "Can only call window.fetch on instances of
    // Window" if fetch is invoked as a bare (unbound) function reference. Android's WebView is lenient.
    const token = await brokeredGoogleSignIn({ fetchImpl: window.fetch.bind(window), openUrl, apiUrl });
    dbg('signing in with custom token');
    await signInWithCustomToken(this.auth, token);
    dbg('signed in');
  }

  async signOut(): Promise<void> {
    await fbSignOut(this.auth);
  }

  /** E2E-only: sign in with a pre-minted custom token (see AuthClient.signInWithToken). */
  async signInWithToken(token: string): Promise<void> {
    await signInWithCustomToken(this.auth, token);
  }
}

/** Build the auth client from env config, or fall back to local-only mode when unconfigured. */
export function createAuthClient(config: FirebaseWebConfig | null = readFirebaseConfig()): AuthClient {
  if (!config) return localOnlyAuthClient;
  const app = initializeApp(config);
  const client = new FirebaseAuthClient(app);
  // E2E-only hooks (TEST-004): real custom-token sign-in AND a synthetic fake-user path so the editor
  // specs run in CI without gcloud/WIF, instead of skipping. See auth/e2eClient.ts. Gated to the
  // VITE_E2E build so neither hook is present in a production bundle.
  if (import.meta.env.VITE_E2E === '1' && typeof window !== 'undefined') {
    return installE2EAuthHooks(client);
  }
  return client;
}
